import { StatesByRegion } from '../slices/locations-slice/models/states-by-region';
import * as yup from 'yup';
import { COMMON_REGEX } from './constants';

export const statesList = [
    { name: 'Alaska', code: 'AK' },
    { name: 'Alabama', code: 'AL' },
    { name: 'Arkansas', code: 'AR' },
    { name: 'Arizona', code: 'AZ' },
    { name: 'California', code: 'CA' },
    { name: 'Colorado', code: 'CO' },
    { name: 'Connecticut', code: 'CT' },
    { name: 'District Of Columbia', code: 'DC' },
    { name: 'Delaware', code: 'DE' },
    { name: 'Florida', code: 'FL' },
    { name: 'Georgia', code: 'GA' },
    { name: 'Hawaii', code: 'HI' },
    { name: 'Iowa', code: 'IA' },
    { name: 'Idaho', code: 'ID' },
    { name: 'Illinois', code: 'IL' },
    { name: 'Indiana', code: 'IN' },
    { name: 'Kansas', code: 'KS' },
    { name: 'Kentucky', code: 'KY' },
    { name: 'Louisiana', code: 'LA' },
    { name: 'Massachusetts', code: 'MA' },
    { name: 'Maryland', code: 'MD' },
    { name: 'Maine', code: 'ME' },
    { name: 'Michigan', code: 'MI' },
    { name: 'Minnesota', code: 'MN' },
    { name: 'Missouri', code: 'MO' },
    { name: 'Mississippi', code: 'MS' },
    { name: 'Montana', code: 'MT' },
    { name: 'North Carolina', code: 'NC' },
    { name: 'North Dakota', code: 'ND' },
    { name: 'Nebraska', code: 'NE' },
    { name: 'New Hampshire', code: 'NH' },
    { name: 'New Jersey', code: 'NJ' },
    { name: 'New Mexico', code: 'NM' },
    { name: 'Nevada', code: 'NV' },
    { name: 'New York', code: 'NY' },
    { name: 'Ohio', code: 'OH' },
    { name: 'Oklahoma', code: 'OK' },
    { name: 'Oregon', code: 'OR' },
    { name: 'Pennsylvania', code: 'PA' },
    { name: 'Rhode Island', code: 'RI' },
    { name: 'South Carolina', code: 'SC' },
    { name: 'South Dakota', code: 'SD' },
    { name: 'Tennessee', code: 'TN' },
    { name: 'Texas', code: 'TX' },
    { name: 'Utah', code: 'UT' },
    { name: 'Virginia', code: 'VA' },
    { name: 'Vermont', code: 'VT' },
    { name: 'Washington', code: 'WA' },
    { name: 'Wisconsin', code: 'WI' },
    { name: 'West Virginia', code: 'WV' },
    { name: 'Wyoming', code: 'WY' },
];

export const stateToCodeMapper = (stateName: string): string => {
    if (!stateName) {
        return '';
    }

    return (
        statesList.find(
            (state) =>
                state.name.toLowerCase() === stateName.trim().toLowerCase()
        )?.code ?? stateName
    );
};

export const stateCodeToNameMapper = (stateCode: string): string => {
    if (!stateCode) {
        return '';
    }

    return (
        statesList.find(
            (state) =>
                state.code.toLowerCase() === stateCode.trim().toLowerCase()
        )?.name ?? stateCode
    );
};

export interface LocationStateItem {
    id: number;
    code: string;
    name: string;
}

export const getAllStatesIndexed = (
    statesByRegionsParam: StatesByRegion[]
): LocationStateItem[] =>
    getAllStates(statesByRegionsParam, false) as LocationStateItem[];

export const getAllStatesFlat = (
    statesByRegionsParam: StatesByRegion[]
): string[] => getAllStates(statesByRegionsParam, true) as string[];

const getAllStates = (
    statesByRegionsParam: StatesByRegion[],
    flat: boolean = false
): LocationStateItem[] | string[] => {
    let result =
        statesByRegionsParam
            ?.flatMap(
                (regionWithStates: StatesByRegion) => regionWithStates.states
            )
            .filter(
                (currentValue: any, index: number, arr: any[]) =>
                    arr.indexOf(currentValue) === index
            )
            .sort() ?? [];

    return flat
        ? result
        : result.map((state: string, index: number) => {
              return {
                  id: index,
                  code: stateToCodeMapper(state),
                  name: stateCodeToNameMapper(state),
              };
          });
};

const validateZipCode = async (
    value: string | undefined,
    axios: any,
    zipCodeCache: Record<string, boolean>
): Promise<boolean> => {
    if (!value || value.toString().length !== 5) {
        return false;
    }

    if (typeof zipCodeCache[value] === 'boolean') {
        return zipCodeCache[value];
    }

    try {
        const response = await axios.get(`api/location/zip/${value}`);
        const isValid = response.status === 200;
        zipCodeCache[value] = isValid;
        return isValid;
    } catch (error) {
        zipCodeCache[value] = false;
        return false;
    }
};

export const isCanadianZipCode = (value: string | undefined): boolean =>
    !!value && COMMON_REGEX.CANADIAN_ZIP_CODE.test(value.trim().toUpperCase());

const validateUSOrCanadianZipCode = async (
    value: string | undefined,
    axios: any,
    zipCodeCache: Record<string, boolean>
): Promise<boolean> => {
    if (isCanadianZipCode(value)) {
        return true;
    }

    return validateZipCode(value, axios, zipCodeCache);
};

export const getZipCodeValidationSchema = (
    axios: any,
    zipCodeCache: Record<string, boolean>,
    options: { required?: boolean; label?: string; allowCanadian?: boolean } = {}
) => {
    const { required = true, label = 'ZIP Code', allowCanadian = false } = options;

    let schema = allowCanadian
        ? yup
              .string()
              .trim()
              .test(
                  'zip',
                  `${label} must be a 5-digit US zip code or a Canadian postal code`,
                  (value) => validateUSOrCanadianZipCode(value, axios, zipCodeCache)
              )
        : yup
              .string()
              .trim()
              .length(5, `${label} must be exactly 5 digits`)
              .matches(COMMON_REGEX.ZIP_CODE, `Invalid ${label} format`)
              .test('zip', `${label} is invalid`, (value) =>
                  validateZipCode(value, axios, zipCodeCache)
              );

    if (required) {
        schema = schema.required(`${label} is required`);
    }

    return schema;
};
